import { useMutation, UseMutationOptions } from "@tanstack/react-query";
import { SignupContextPayload } from "pages/auth/tracking";

import { authClient, verificationCallbackUrl } from "lib/auth/authClient";
import { awsWafTokenHeaders } from "lib/auth/awsWafToken";
import { SKIP_SENTRY_FOR_CLIENT_ERRORS } from "lib/sentryErrors";
import { BetterAuthError, BetterAuthUserData } from "./utils";

type SignUpVariables = {
  name: string;
  email: string;
  password: string;
  // Optional post-verification destination (sanitised internal path) and marketing attribution
  // captured from the register URL. Both survive the email round-trip via better-auth.
  redirect?: string;
  signupContext?: SignupContextPayload;
};

type SignUpResponseData = {
  token?: string | null;
  user?: BetterAuthUserData;
};

/**
 * The auth-service has `requireEmailVerification: true`, so a successful sign-up does NOT create
 * a session — it sends a verification email and the user must confirm before signing in. Callers
 * should show a "check your email" state rather than redirecting into the app.
 * @returns
 */
export function useSignUp(
  options?: UseMutationOptions<SignUpResponseData, BetterAuthError, SignUpVariables, unknown>,
) {
  const mutationOptions: UseMutationOptions<
    SignUpResponseData,
    BetterAuthError,
    SignUpVariables,
    unknown
  > = {
    meta: SKIP_SENTRY_FOR_CLIENT_ERRORS,
    mutationKey: ["signUp"],
    ...options,
    mutationFn: async ({ name, email, password, redirect, signupContext }: SignUpVariables) => {
      // Anti-bot: attach a WAF token to this request (see awsWafToken.ts).
      const wafHeaders = await awsWafTokenHeaders();
      const { data, error } = await authClient.signUp.email(
        {
          name,
          email,
          password,
          // Thread the sanitised redirect through so the verification link returns the user there.
          callbackURL: verificationCallbackUrl({ email, redirect }),
          // auth-service reads these off the request body, but better-auth's client types don't
          // declare them. Spread rather than list them, since a spread skips excess-property
          // checking. `signupContext` is marketing attribution; see signup-context.ts.
          ...(signupContext ? { signupContext: JSON.stringify(signupContext) } : {}),
        },
        { headers: wafHeaders },
      );
      if (error) {
        throw error;
      }
      return data;
    },
  };
  return useMutation(mutationOptions);
}
